import React, { FC, ReactElement } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { FormNodeDealerOptionModel } from '../../../../../shared/dealer';
import { useTranslation } from '../../../../translations';
import { Fetcher, useFetcher } from '../../../../utils';
import {
  AbstractElementEditor,
  UpdateFormProps,
  UpdateFormPropsNoTrans,
} from './AbstractElementEditor';

export class DealerOptionEditor extends AbstractElementEditor<FormNodeDealerOptionModel> {
  public getDescription(): string {
    return 'Entry for an option for a dealer.';
  }

  public getName(): string {
    return 'Dealer Option';
  }

  public getIcon(): string {
    return 'settings';
  }

  public override getDefaultValues(): Partial<Omit<FormNodeDealerOptionModel, 'id' | 'type'>> {
    return {
      optionId: -1,
    };
  }

  public override getUpdateForm({
    node,
    update,
  }: UpdateFormProps<FormNodeDealerOptionModel>): ReactElement {
    return <DealerOptionUpdateForm node={node} update={update} />;
  }
}

// eslint-disable-next-line @typescript-eslint/no-deprecated
const DealerOptionUpdateForm: FC<UpdateFormPropsNoTrans<FormNodeDealerOptionModel>> = ({
  node,
  update,
}) => {
  const { ts } = useTranslation();

  const options = useFetcher(async () => {
    return await api.getOptions('dealer');
  }, []);

  if (!options.data) {
    return <Fetcher result={options} />;
  }

  return (
    <>
      <Label for="dealerOptionId">{ts('option')}</Label>
      <Input
        defaultValue={node.optionId}
        id="dealerOptionId"
        onChange={(e) => {
          const optionId = Number.parseInt(e.target.value, 10);
          update({ ...node, optionId });
        }}
        type="select"
      >
        <option disabled value={-1} />
        {options.data.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </Input>
      <Label for="dealerOptionDisplay">{ts('option_editor_select.display_name_optional')}</Label>
      <Input
        defaultValue={node.displayName ?? ''}
        id="dealerOptionDisplay"
        onChange={(e) => {
          update({ ...node, displayName: e.target.value });
        }}
        type="text"
      />
      <FormGroup check className="py-2">
        <Input
          checked={node.required ?? false}
          id="dealerOptionRequired"
          onChange={(e) => {
            update({ ...node, required: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="dealerOptionRequired">
          {ts('required')}
        </Label>
      </FormGroup>
      <FormGroup check>
        <Input
          checked={node.applicationOnly ?? false}
          id="dealerOptionApplicationOnly"
          onChange={(e) => {
            update({ ...node, applicationOnly: e.target.checked });
          }}
          type="checkbox"
        />
        <Label check for="dealerOptionApplicationOnly">
          {ts('prevent_changes_to_this_option_after_application_is_submitted')}
        </Label>
      </FormGroup>
    </>
  );
};
